/* eslint-disable no-restricted-syntax */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { CustomAttributeData } from '../../../customAttributes/customAttributes';
import { PriceData } from '../../../fileFormats/catalog/catalog';
import { CustomAttributeOptionPropertiesData } from '../../../customAttributes/customAttributesOptionProperties';

interface ComponentCustomAttributeOptionProps {
  attribute: CustomAttributeData,
  value: string,
  quantity: number | undefined,
  onValueChanged: (updatedValue: string) => void,
  onQuantityChanged: ((updatedQuantity: number) => void) | undefined,
  editable: boolean,
  priceCurrencySymbol: string,
  priceData: PriceData[] | undefined,
  conditionalRangeMax : string | undefined,
  conditionalRangeMin: string | undefined,
}

export function ComponentCustomAttributeOption({
  attribute,
  value,
  quantity,
  onValueChanged,
  onQuantityChanged,
  editable,
  priceCurrencySymbol,
  priceData,
  conditionalRangeMax,
  conditionalRangeMin,
}: ComponentCustomAttributeOptionProps): JSX.Element {

  const [globalShowPrices, setGlobalShowPrices] = useState<boolean>(false);

  useEffect(() => {
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);

    // Get the value of the CSS variable and interpret it
    const showPricesValue = computedStyle.getPropertyValue('--show-prices').trim();
    setGlobalShowPrices(showPricesValue === 'true');
  }, []);

  const [valueChanged, setValueChanged] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (valueChanged !== undefined) {
      const aVal = valueChanged;
      onValueChanged(aVal);
      setValueChanged(undefined);
    }
  }, [valueChanged]);

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedQuantity = Math.max(0, parseInt(event.target.value, 10) || 0);
    if (onQuantityChanged !== undefined) {
      onQuantityChanged(updatedQuantity);
    }
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const updatedValue = event.target.value;
    if (onValueChanged !== undefined) {
      onValueChanged(updatedValue);
    }
  };

  const filterOptionRange = (attr : CustomAttributeData) : string [] | undefined => {
    if (!attr.propertiesOption?.options) return undefined;

    if (conditionalRangeMax === undefined && conditionalRangeMin === undefined) {
      return attr.propertiesOption?.options;
    }
    const { options } = attr.propertiesOption;
    const effectiveOptions: string[] = [];

    const min = conditionalRangeMin;
    const max = conditionalRangeMax;

    // Helper to check if a string is a valid number
    const toNum = (v: string): number | undefined => {
      const n = Number(v.replace(',', '.'));
      return Number.isNaN(n) ? undefined : n;
    };

    for (const opt of options) {
      // Always include empty string
      if (opt === '') {
        effectiveOptions.push(opt);
        continue;
      }
      if (min === '' && max === '') continue;

      const optNum = toNum(opt.replace(',', '.'));
      const minNum = min !== undefined ? toNum(min) : undefined;
      const maxNum = max !== undefined ? toNum(max) : undefined;

      let include = true;

      // Decide numeric vs string comparison
      const numericMode = optNum !== undefined
        && (min === undefined || minNum !== undefined)
        && (max === undefined || maxNum !== undefined);

      if (numericMode) {
        if (minNum !== undefined && optNum < minNum) include = false;
        if (maxNum !== undefined && optNum > maxNum) include = false;
      } else {
        if (min !== undefined && opt < min) include = false;
        if (max !== undefined && opt > max) include = false;
      }

      if (include) {
        effectiveOptions.push(opt);
      }
    }

    return effectiveOptions;
  };

  const getTranslatedOption = (option: string, attr: CustomAttributeData) : string => {
    if (attr.propertiesOption && attr.propertiesOption.optionTranslations && attr.propertiesOption.optionTranslations.length > 0) {
      return CustomAttributeOptionPropertiesData.translateOption(
        attr.propertiesOption,
        option,
        attr.selectedLang || 'pt',
      );
    }
    return option;
  };

  if (attribute.propertiesOption === undefined || attribute.propertiesOption.options === undefined) {
    return <div />;
  }

  // Clip based on max and min ranges
  const effectiveOptions = filterOptionRange(attribute);

  let realValue = effectiveOptions?.find((v) => { return !v.localeCompare(value); });

  if (realValue === undefined) {
    // eslint-disable-next-line prefer-destructuring
    realValue = effectiveOptions?.[0];

    if (valueChanged !== realValue) {
      setValueChanged(realValue);
    }
  }

  const isNullOption = realValue === ' ' || realValue === '';
  const basePrice = realValue === ' ' || realValue === '' ? 0 : CustomAttributeData.getAttributeValuePrice(attribute.key, value, attribute.basePrice, priceData);
  const priceTotal = quantity !== undefined ? basePrice * quantity : 0;
  const roundedPriceTotal = Math.round(priceTotal * 100) / 100;
  const totalPriceWithCurrency = `${roundedPriceTotal.toFixed(2)} ${priceCurrencySymbol}`;
  // const totalPriceWithCurrency = `${priceTotal} ${priceCurrencySymbol}`;
  const basePriceWithCurrency = `${basePrice} ${priceCurrencySymbol}`;

  // TODO PMC: Read from Redux Slice "config" the SHOW_PRICE and set accordingly
  const showPrice = globalShowPrices && basePrice > 0;

  return (
    <>
      <div className="col-8">
        <div className="row">
          <div className="col-5">
            <b style={{ color: attribute.color || '#000000' }}>{attribute.name}</b>
          </div>
          <div className="col-7">
            <select
              id={attribute.key}
              name={attribute.name}
              value={realValue}
              onChange={handleValueChange}
            >
              {/* Add the remaining options */}
              {effectiveOptions?.map((option, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <option key={index + 1} value={option}>
                  {getTranslatedOption(option, attribute)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div
        className="col-4"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div
          className="col-4 attribute-value-medium"
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'right',
            alignItems: 'center',
          }}
        >
          {(!isNullOption && showPrice) && (
            <div style={{ marginLeft: '30px' }}>{basePriceWithCurrency}</div>
          )}
          {(!isNullOption && showPrice && attribute.hasQuantity && quantity !== undefined && quantity >= 0) && (
            <div style={{ marginLeft: '10px' }}> x </div>
          )}
          {(!isNullOption && attribute.hasQuantity && quantity !== undefined && quantity >= 0) && (
            editable ? (
              <input
                type="number"
                value={quantity}
                min={0}
                step={1}
                onChange={handleQuantityChange}
                style={{
                  marginLeft: '10px', width: '70px', textAlign: 'center',
                }}
              />
            ) : (
              <input
                type="number"
                value={quantity}
                disabled
                style={{ marginLeft: '10px', width: '105px', background: '#e9ecef' }}
              />
            )
          )}
          {(!isNullOption && showPrice && attribute.hasQuantity && quantity !== undefined && quantity >= 0) && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ marginLeft: '10px' }}>=</div>
              <div style={{ marginLeft: '5px', width: '70px', textAlign: 'right' }}>{totalPriceWithCurrency}</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
